import { Box, Tab, Tabs, Typography } from "@mui/material";
import React, { SyntheticEvent, useState } from "react";
import PersonAssociatedVersionsTable from "../../../components/tables/PersonAssociatedVersionsTable";
import { RoleItem } from "../../../types/person";
import {
  SYSTEM_TEAM_CONTRIBUTION_TYPE_LABELS,
  SystemTeamContributionTypes,
} from "../../../types/versionTeam";

interface RoleMergeMap {
  personalRole: string;
  teamRole: string;
}

interface RoleTabProps {
  personId: string;
  role: {
    title: string;
    personalRole?: string;
    teamRole?: string;
  };
  languages?: string[];
}

interface PersonAssociatedVersionsSectionProps {
  personId: string;
  personalRoles?: RoleItem[];
  teamRoles?: RoleItem[];
}

const ROLES_TO_MERGE: RoleMergeMap[] = [
  {
    personalRole: "translator",
    teamRole: "translators",
  },
];

const generateRoleTabs = ({
  personId,
  personalRoles = [],
  teamRoles = [],
}: PersonAssociatedVersionsSectionProps): RoleTabProps[] => {
  const mergedMap = new Map<string, RoleTabProps>();
  for (const { role, languages } of personalRoles) {
    mergedMap.set(role, {
      personId,
      role: {
        title: Object.values(SystemTeamContributionTypes).includes(
          role as SystemTeamContributionTypes
        )
          ? SYSTEM_TEAM_CONTRIBUTION_TYPE_LABELS[
              role as keyof typeof SYSTEM_TEAM_CONTRIBUTION_TYPE_LABELS
            ]
          : role,
        personalRole: role,
      },
      languages: languages,
    });
  }

  for (const { role, languages } of teamRoles) {
    mergedMap.set(role, {
      personId,
      role: {
        title: Object.values(SystemTeamContributionTypes).includes(
          role as SystemTeamContributionTypes
        )
          ? SYSTEM_TEAM_CONTRIBUTION_TYPE_LABELS[
              role as keyof typeof SYSTEM_TEAM_CONTRIBUTION_TYPE_LABELS
            ]
          : role,
        teamRole: role,
      },
      languages: languages,
    });
  }

  for (const { personalRole, teamRole } of ROLES_TO_MERGE) {
    const personalRoleItem = mergedMap.get(personalRole);
    const teamRoleItem = mergedMap.get(teamRole);

    if (personalRoleItem && teamRoleItem) {
      mergedMap.delete(personalRole);
      mergedMap.delete(teamRole);
      mergedMap.set(personalRoleItem.role.title, {
        personId,
        role: {
          title: personalRoleItem.role.title,
          personalRole: personalRoleItem.role.personalRole,
          teamRole: teamRoleItem.role.teamRole,
        },
        languages: Array.from(
          new Set([
            ...(personalRoleItem.languages || []),
            ...(teamRoleItem.languages || []),
          ])
        ),
      });
    }
  }

  return Array.from(mergedMap.values())
    .map((item) => ({
      ...item,
      languages: item.languages
        ? item.languages.sort((a, b) => a.localeCompare(b))
        : [],
    }))
    .sort((a, b) => a.role.title.localeCompare(b.role.title));
};

const RoleTabs: React.FC<PersonAssociatedVersionsSectionProps> = ({
  personId,
  personalRoles = [],
  teamRoles = [],
}) => {
  const [selectedRoleIndex, setSelectedRoleIndex] = useState(-1);

  const handleRoleChange = (e: SyntheticEvent, newIndex: number) =>
    setSelectedRoleIndex(newIndex);

  const roles = generateRoleTabs({ personId, personalRoles, teamRoles });

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={selectedRoleIndex}
          onChange={handleRoleChange}
          aria-label="Role Tabs"
        >
          <Tab label="All Roles" value={-1} />
          {roles.map((item, index) => (
            <Tab key={index} label={item.role.title} value={index} />
          ))}
        </Tabs>
      </Box>

      {selectedRoleIndex === -1 ? (
        <Box mt={2}>
          <PersonAssociatedVersionsTable personId={personId} showRole showTeam />
        </Box>
      ) : (
        <Box mt={2}>
          <RoleTab key={selectedRoleIndex} {...roles[selectedRoleIndex]} />
        </Box>
      )}
    </>
  );
};

const RoleTab: React.FC<RoleTabProps> = ({
  personId,
  role,
  languages = [],
}) => {
  const { personalRole, teamRole } = role;
  const [selectedLanguageIndex, setSelectedLanguageIndex] = useState(-1);

  const handleLanguageChange = (e: SyntheticEvent, newIndex: number) =>
    setSelectedLanguageIndex(newIndex);

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={selectedLanguageIndex}
          onChange={handleLanguageChange}
          aria-label="Languages Tabs"
        >
          <Tab label="All Languages" value={-1} />
          {languages.map((language, index) => (
            <Tab key={index} label={language} value={index} />
          ))}
        </Tabs>
      </Box>
      {selectedLanguageIndex === -1 ? (
        <Box mt={2}>
          <PersonAssociatedVersionsTable
            personId={personId}
            personalRole={personalRole}
            teamRole={teamRole}
            showTeam={!!teamRole}
          />
        </Box>
      ) : (
        <Box mt={2}>
          <PersonAssociatedVersionsTable
            personId={personId}
            personalRole={personalRole}
            teamRole={teamRole}
            showTeam={!!teamRole}
            language={languages[selectedLanguageIndex]}
          />
        </Box>
      )}
    </>
  );
};

const PersonAssociatedVersionsSection: React.FC<
  PersonAssociatedVersionsSectionProps
> = ({ personId, personalRoles = [], teamRoles = [] }) => {
  return (
    <>
      <Typography variant="h2" mb={2}>
        Associated Versions
      </Typography>
      <RoleTabs
        personalRoles={personalRoles}
        teamRoles={teamRoles}
        personId={personId}
      />
    </>
  );
};

export default PersonAssociatedVersionsSection;
